import { useMemo, useState } from "react";
import { RotateCcw, Download, Trash2, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLoad, act } from "@/lib/hooks";
import { fdatetime, api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAccess } from "@/context/AuthContext";
import { PageHeader, Section, EmptyRow, Field, NativeSelect } from "@/components/common";

const BLANK = { from: "", to: "", action: "", user: "" };
const nice = (s) => (s || "").replace(/_/g, " ");

async function exportCsv(f) {
  const params = Object.fromEntries(Object.entries({ date_from: f.from, date_to: f.to, action: f.action, user: f.user }).filter(([, v]) => v));
  try {
    const r = await api.get("/tenant/audit/export", { params, responseType: "blob" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(r.data);
    el.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
    el.click();
  } catch (e) { toast.error(errMsg(e)); }
}

function Filters({ f, setF, actions, users, shown }) {
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div className="po-card grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="audit-filters">
      <Field label="From"><Input type="date" data-testid="audit-filter-from" value={f.from} onChange={(e) => set("from")(e.target.value)} /></Field>
      <Field label="To"><Input type="date" data-testid="audit-filter-to" value={f.to} onChange={(e) => set("to")(e.target.value)} /></Field>
      <Field label="Action type"><NativeSelect testId="audit-filter-action" value={f.action} onChange={set("action")} options={[{ value: "", label: "All actions" }, ...actions.map((a) => ({ value: a, label: nice(a) }))]} /></Field>
      <Field label="User"><NativeSelect testId="audit-filter-user" value={f.user} onChange={set("user")} options={[{ value: "", label: "All users" }, ...users.map((u) => ({ value: u, label: u }))]} /></Field>
      <div className="flex flex-wrap items-end gap-2 sm:col-span-2 lg:col-span-1"><Button variant="outline" data-testid="audit-filter-reset" disabled={JSON.stringify(f) === JSON.stringify(BLANK)} onClick={() => setF(BLANK)}><RotateCcw className="mr-1 h-4 w-4" />Reset</Button>
        <Button variant="outline" data-testid="audit-export-csv" disabled={!shown.length} onClick={() => exportCsv(f)}><Download className="mr-1 h-4 w-4" />Export CSV</Button></div>
    </div>);
}

function LoggingSettings({ canEdit }) {
  const [s, reload] = useLoad("/tenant/audit/settings");
  if (!s) return null;
  const toggle = async (type, on) => {
    const disabled = on ? s.disabled.filter((x) => x !== type) : [...s.disabled, type];
    if (await act(() => api.put("/tenant/audit/settings", { disabled }), "Audit logging updated")) reload();
  };
  return <Section title="Audit logging settings" className="mb-4"><div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="audit-settings">
    {s.types.map((t) => <label key={t} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm capitalize">
      {nice(t)}<Switch data-testid={`audit-toggle-${t}`} disabled={!canEdit} checked={!s.disabled.includes(t)} onCheckedChange={(v) => toggle(t, v)} /></label>)}
  </div></Section>;
}

function Entry({ a, onDelete }) {
  return <div className="space-y-1 border-b border-slate-100 px-4 py-3 text-sm last:border-0" data-testid="audit-card">
    <div className="flex items-start justify-between gap-2"><span className="font-medium text-slate-800">{a.description}</span>
      {onDelete && <button aria-label="Delete entry" data-testid={`audit-delete-m-${a.id}`} onClick={() => onDelete(a)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}</div>
    <div className="text-xs text-slate-500">{fdatetime(a.at)} · {a.user_name} · <span className="capitalize">{nice(a.action)}</span>{a.reason ? ` · ${a.reason}` : ""}</div>
  </div>;
}

export default function AuditLog() {
  const [rows, reload] = useLoad("/tenant/audit");
  const [f, setF] = useState(BLANK);
  const [del, setDel] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const { canWrite } = useAccess();
  const admin = canWrite(["tenant_admin"]);
  const actions = useMemo(() => [...new Set((rows || []).map((a) => a.action))].sort(), [rows]);
  const users = useMemo(() => [...new Set((rows || []).map((a) => a.user_name).filter(Boolean))].sort(), [rows]);
  const shown = (rows || []).filter((a) => (!f.from || a.at.slice(0, 10) >= f.from) && (!f.to || a.at.slice(0, 10) <= f.to) && (!f.action || a.action === f.action) && (!f.user || a.user_name === f.user));
  const remove = async () => { if (await act(() => api.delete(`/tenant/audit/${del.id}`), "Entry deleted")) { setDel(null); reload(); } };
  return (
    <>
      <PageHeader title="Audit trail" subtitle="Every unlock, edit, reversal and settings change in this workspace.">
        <Button variant="outline" data-testid="audit-settings-button" onClick={() => setShowSettings(!showSettings)}><Settings2 className="mr-1 h-4 w-4" />Logging settings</Button>
      </PageHeader>
      {showSettings && <LoggingSettings canEdit={admin} />}
      <Filters f={f} setF={setF} actions={actions} users={users} shown={shown} />
      <Section>
        <div className="px-5 pt-3 text-xs text-slate-500" data-testid="audit-count">{rows ? `${shown.length} of ${rows.length} entries${rows.length >= 1000 ? " (table shows the latest 1,000; Export CSV includes every matching entry)" : ""}` : ""}</div>
        <div className="md:hidden" data-testid="audit-cards">{rows && !shown.length && <p className="p-4 text-sm text-slate-400">No entries match these filters.</p>}
          {shown.map((a) => <Entry key={a.id} a={a} onDelete={admin ? setDel : null} />)}</div>
        <div className="hidden overflow-x-auto md:block"><table className="po-table" data-testid="audit-table">
          <thead><tr><th>When</th><th>User</th><th>What changed</th><th>Action</th><th>Reason</th>{admin && <th />}</tr></thead>
          <tbody>
            {rows && shown.length === 0 && <EmptyRow cols={6} text={rows.length ? "No entries match these filters." : undefined} />}
            {shown.map((a) => (
              <tr key={a.id} data-testid="audit-row">
                <td className="whitespace-nowrap">{fdatetime(a.at)}</td><td>{a.user_name}</td>
                <td className="max-w-md whitespace-normal" data-testid="audit-description">{a.description}</td>
                <td className="capitalize font-medium">{nice(a.action)}</td><td className="text-slate-500">{a.reason || "—"}</td>
                {admin && <td><button aria-label="Delete entry" data-testid={`audit-delete-${a.id}`} onClick={() => setDel(a)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></td>}
              </tr>
            ))}
          </tbody>
        </table></div>
      </Section>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="audit-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this audit entry?</AlertDialogTitle>
            <AlertDialogDescription>{del?.description}. This can't be undone; the deletion itself is recorded in the audit trail.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="audit-delete-cancel">Cancel</AlertDialogCancel><AlertDialogAction data-testid="audit-delete-confirm" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
